import capitalize from 'lodash-es/capitalize'
import { RouteInfo } from '../../types/state'
import { isLounge } from '../mfcLogic/userProperties/user'

export type PageRoute =
    | '_error'
    | '404'
    | 'chat-archives'
    | 'friends'
    | 'home'
    | 'mail'
    | 'message'
    | 'messages'
    | 'miss-mfc'
    | 'model-browser'
    | 'news'
    | 'notes'
    | 'private-gallery'
    | 'profile'
    | 'recommendations'
    | 'room'
    | 'rooms'
    | 'search'

export interface PageMetadata {
    visible: boolean
    description?: string
    dynamic?: boolean
    getTitle(username?: string): string
    lensEvent?: string
    lensData?:
        | Record<string, unknown>
        | ((routeInfo: RouteInfo) => Record<string, unknown>)
}

type PageMetadataConfig = Record<PageRoute, PageMetadata>

export const pageMetadata: PageMetadataConfig = {
    _error: { visible: false, getTitle: () => 'Error' },
    '404': { visible: false, getTitle: () => 'Error' },
    'chat-archives': {
        visible: false,
        getTitle: (username) => {
            return username ? `Archive - ${username.toString()}` : 'Archive'
        },
        lensEvent: 'Archives_Click',
    },
    friends: {
        visible: false,
        getTitle: () => 'Friends',
        lensEvent: 'ManageFriends_Click',
    },
    home: {
        visible: true,
        description:
            'MyFreeCams is the original free webcam community for adults, featuring live video chat with thousands of models, cam girls, amateurs and female content creators!',
        getTitle: () => 'Home',
        lensEvent: 'Homepage_Click',
    },
    mail: {
        visible: false,
        getTitle: () => 'Mail',
        lensEvent: 'MfcMail_Click',
    },
    message: {
        visible: false,
        getTitle: (username) => {
            return `PM with ${username}`
        },
        lensEvent: 'PrivateMessage_Click',
    },
    messages: {
        visible: false,
        getTitle: () => 'Private Messages',
        lensEvent: 'MessagesList_Click',
    },
    'miss-mfc': {
        visible: true,
        description:
            'View Miss MyFreeCams rankings. The model that earns the most tokens each month is crowned Miss MyFreeCams!',
        getTitle: () => 'Miss MFC',
        lensEvent: 'MissMFC_Click',
    },
    'model-browser': {
        visible: true,
        description:
            'Browse MyFreeCams models category, rankings, tags, topic, and more.',
        getTitle: () => 'Model Browser',
        lensEvent: 'ModelBrowser_Click',
    },
    news: {
        visible: true,
        description:
            'View and search recent news updates posted by models on MyFreeCams.',
        getTitle: () => 'News',
        lensEvent: 'NewsFeed_Click',
    },
    notes: {
        visible: false,
        getTitle: () => 'My Notes',
        lensEvent: 'Notes_Click',
    },
    'private-gallery': {
        visible: false,
        getTitle: () => 'Gallery',
        lensEvent: 'PrivateGallery_Click',
    },
    profile: {
        visible: true,
        dynamic: true,
        getTitle: (username) => {
            return `${username}`
        },
        lensEvent: 'Profile_Click',
        lensData: ({ pendingRoute, pendingQuery }) => ({
            isLounge: isLounge(pendingQuery?.slug || pendingRoute),
        }),
    },
    recommendations: {
        visible: false,
        description:
            'Browse MyFreeCams for models you may like and view recommendations.',
        getTitle: (username) => {
            if (!username) return `Models You May Like`
            const displayName = capitalize(username)
            return `${displayName} - Models You May Like`
        },
        lensEvent: 'Recommend_Click',
        lensData: { page: true },
    },
    room: {
        visible: true,
        dynamic: true,
        getTitle: (username) => {
            return isLounge(username) ? username : `${username}'s Room`
        },
        lensEvent: 'ChatRoom_Click',
        lensData: ({ pendingSlug }) => ({ isLounge: isLounge(pendingSlug) }),
    },
    rooms: {
        visible: false,
        description: 'View recently your recently visited rooms on MyFreeCams.',
        getTitle: () => 'Recently Visited Rooms',
        lensEvent: 'RoomsList_Click',
    },
    search: {
        visible: true,
        description:
            'Search and explore MyFreeCams for popular, trending, new models, Miss MFC, and more!',
        getTitle: () => 'Search',
        lensEvent: 'Explore_Click',
        lensData: ({ pendingQuery }) => ({ type: pendingQuery?.type }),
    },
}

export function getPageMetadata(page: string): PageMetadata | undefined {
    return pageMetadata[page || 'home']
}

export function getPageMetadataByPath(pathname: string): PageMetadata {
    return getPageMetadata(pathname === '/' ? '' : pathname.split('/')[1])
}

export const createPageTitle = (pageTitle) => {
    const finalTitle =
        typeof pageTitle === 'string'
            ? `${pageTitle} - MyFreeCams App`
            : 'MyFreeCams - The #1 adult webcam community!'
    return finalTitle
}
